import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface VehicleInterestCardProps {
  vehicle: {
    id: string;
    year: number;
    make: string;
    model: string;
    trim?: string;
    stock_number?: string;
    asking_price?: number;
    price?: number;
    image?: string;
    bhph_eligible: boolean;
    bhph_min_down_type?: string;
    bhph_min_down_value?: number;
    bhph_payment_amount?: number;
    bhph_payment_frequency?: string;
    bhph_term_months?: number;
  };
  onRemove?: () => void;
  compact?: boolean;
}

const fmtCurrency = (v?: number) =>
  v != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(v)
    : '—';

const VehicleInterestCard: React.FC<VehicleInterestCardProps> = ({
  vehicle,
  onRemove,
  compact,
}) => {
  const v = vehicle;
  const downLabel =
    v.bhph_min_down_type === 'percent'
      ? `${v.bhph_min_down_value}% down`
      : fmtCurrency(v.bhph_min_down_value) + ' down';
  const freqLabel = v.bhph_payment_frequency === 'biweekly' ? 'bi-weekly' : 'mo';

  return (
    <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
      <div className="w-16 h-12 rounded bg-gray-200 overflow-hidden flex-shrink-0">
        {v.image ? (
          <img
            src={v.image}
            alt={`${v.year} ${v.make} ${v.model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
            No img
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">
            {v.year} {v.make} {v.model} {v.trim || ''}
          </span>
          {v.bhph_eligible ? (
            <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
              BHPH
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
              3rd Party
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
          {v.stock_number && <span>#{v.stock_number}</span>}
          <span>{fmtCurrency(v.asking_price ?? v.price)}</span>
          {v.bhph_eligible && v.bhph_payment_amount && !compact && (
            <span className="text-green-700">
              {fmtCurrency(v.bhph_payment_amount)}/{freqLabel} | {downLabel} | {v.bhph_term_months}
              mo
            </span>
          )}
        </div>
      </div>

      {onRemove && (
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export default VehicleInterestCard;
